/**
 * @file Album folder related queries
 */

import gql from 'graphql-tag';

import { ActionableItemFields } from '../fragments/actionable-items';
import {
  AlbumFolderDetails,
  AlbumFolderChildrenDetails,
  AlbumFolderWatermarkConfig,
  AlbumFolderBaseDetails,
  AlbumFolderWithChildrenData,
  AlbumFolderPhotos,
  AlbumFolderPhotosDetails,
  AlbumFolderForReviewDetails,
  AlbumFolderHeroImageConfig,
  AlbumFolderLogoImageConfig,
  AlbumFolderMainCoverPhoto,
  AlbumFolderAlbumCustomPrintForPurchaseDetails,
  AlbumFolderPricingDetails,
  AlbumFolderChildrenCount,
  AlbumFolderPROChildrenDetails,
  AlbumFolderShutterbugDetails,
  AlbumPhotoFaceHighlightsForAlbumFolder,
  AlbumFolderEventSettingsFields,
  AlbumFolderPurchasedEventAddonDetails,
  AlbumFolderPhotoFinderDetails,
  AlbumFolderTaxDetails,
  AlbumFolderContributeReminderDetails,
  AlbumFolderBroadcastSequencesDetails,
  AlbumFolderDiscountTemplateDetails,
  AlbumFolderHierarchyGradeDetails,
  AlbumFolderYearbookSettings,
  AlbumFolderHierarchyShippingSettings,
  AlbumFolderPrintOrderConfiguration,
  AlbumFolderPrintAssets,
  AlbumFolderBlackoutConfig,
  AlbumFolderIdCardSettings,
} from '../fragments/album-folder';
import {
  AlbumDetailsWithFolderSubscription,
  AlbumListingDetails,
  PhotoThumbnails,
  AlbumForReview,
  PhotoUrlTypeFields,
  AlbumDetails,
  AlbumBasicDetails,
  AlbumHierarchyGradeDetails,
  AlbumLocationFields,
} from '../fragments/album';
import { AlbumPhotoGroupBasicDetails } from '../fragments/album-photo-group';
import {
  UserDetails,
  UserDetailsWithImages,
  VendorDetails,
} from '../fragments/account';
import {
  AccountIdentityName,
  AlbumFolderIdentityDetails,
  IdentityHasRosterIdentityDetails,
  IdentityRosterIdentityDetails,
} from '../fragments/identity';
import {
  AutoContributorSettingsDetails,
  ContributorAssignmentBaseDetails,
  ContributorAssignmentShareBaseDetails,
} from '../fragments/contributor-assignments';
import { TransactionItemDetails } from '../fragments/transaction';
import { VideoDetails } from '../fragments/video';
import {
  PrintPackageDetails,
  PrintPackageForPurchaseDetails,
} from '../fragments/print-packages';
import {
  AlbumAlbumFolderTagDetails,
  AlbumFolderTagDetails,
} from '../fragments/album-folder-tags';
import { AlbumFolderLinkRequestDetails } from '../fragments/picture-day';
import {
  AlbumPhotoFaceHighlightDetails,
  PhotoFavoriteDetails,
  PhotoForegroundDetails,
} from '../fragments/photo';
import {
  AccountPromoCodeDetails,
  PromoCodeDetails,
} from '../fragments/promo-codes';
import { PhotoPricingForPurchaseDetails } from '../fragments/photo-pricings';
import { PurchasedCustomPrintFields } from '../fragments/pro-custom-prints';
import {
  AlbumCustomPrintForPurchaseDetails,
  AlbumFolderCustomPrintForPurchaseDetails,
} from '../fragments/custom-prints';
import { DigitalBundleDetails } from '../fragments/digital-bundles';
import {
  IdentityYearbookPhotoDetails,
  MatchedPhotoYearbookPhotoDetails,
} from '../fragments/yearbook-photos';
import { YearbookExportDetails } from '../fragments/yearbook-exports';
import { ReferenceFaceDetails } from '../fragments/reference-faces';

export const AlbumFolderPRO = gql`
  query albumFolderPro(
    $id: RecordID!
    $include_identities_count: Boolean = false
  ) {
    albumFolder(id: $id) {
      id
      is_root
      school_code
      joins_enabled
      checkout_disabled
      album_photos {
        total
      }
      ...AlbumFolderIdCardSettings
      face_recognition_disabled
      ...AlbumFolderDetails
      ...AlbumFolderPROChildrenDetails
      ...AlbumFolderWatermarkConfig
      ...AlbumFolderForReviewDetails
      ...AlbumFolderHeroImageConfig
      ...AlbumFolderLogoImageConfig
      ...AlbumFolderMainCoverPhoto
      ...AlbumFolderPricingDetails
      ...AlbumFolderShutterbugDetails
      ...AlbumFolderPhotosDetails
      ...AlbumFolderBlackoutConfig
      ...AlbumFolderPurchasedEventAddonDetails
      ...AlbumFolderTaxDetails
      ...AlbumFolderBroadcastSequencesDetails
      ...AlbumFolderDiscountTemplateDetails
      ...AlbumFolderYearbookSettings
      ...AlbumFolderHierarchyShippingSettings
      ...AlbumFolderPrintOrderConfiguration
      ...AlbumFolderPrintAssets
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
      has_album
      album_custom_prints {
        total
      }
      public_join_code_verification_enabled
      actionable_items(slice: { limit: 0 }) {
        total
      }
      tags(album_folder_tag_types: [UNIFORM]) {
        total
      }
      marketing_materials_url
      link_requests(statuses: PENDING, link_relationship: TARGET) {
        total
      }
      identities(slice: { limit: 1 }) @include(if: $include_identities_count) {
        total
      }
      default_matches_only_setting
      print_partner {
        id
        name
        api_type
      }
      all_photos_mode
      enable_external_upload
      is_eligible_for_hierarchy_transfer
      account_for_promo_codes: account {
        id
        promo_codes(
          slice: { limit: 999 }
          album_folder_id: $id
          active_status: ALL
        ) {
          total
          records {
            ...AccountPromoCodeDetails
          }
        }
      }
      purchased_event {
        id
      }
      purchased_custom_prints(slice: { limit: 1 }) {
        total
      }
      pending_purchased_custom_prints: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [PENDING]
      ) {
        total
      }
      purchased_specialty_prints: purchased_custom_prints(
        slice: { limit: 1 }
        custom_print_subtype: SPECIALTY_PRINT
      ) {
        total
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderPROChildrenDetails}
  ${AlbumFolderWatermarkConfig}
  ${AlbumFolderLogoImageConfig}
  ${AlbumFolderForReviewDetails}
  ${AlbumFolderHeroImageConfig}
  ${AlbumFolderMainCoverPhoto}
  ${AlbumFolderPricingDetails}
  ${AlbumFolderShutterbugDetails}
  ${AlbumFolderPhotosDetails}
  ${AlbumFolderEventSettingsFields}
  ${AccountPromoCodeDetails}
  ${AlbumFolderPurchasedEventAddonDetails}
  ${AlbumFolderTaxDetails}
  ${AlbumFolderBroadcastSequencesDetails}
  ${AlbumFolderDiscountTemplateDetails}
  ${AlbumFolderYearbookSettings}
  ${AlbumFolderHierarchyShippingSettings}
  ${AlbumFolderPrintOrderConfiguration}
  ${AlbumFolderPrintAssets}
  ${AlbumFolderBlackoutConfig}
  ${AlbumFolderIdCardSettings}
`;

export const AlbumFolderForContributor = gql`
  query albumFolderForContributor(
    $id: RecordID!
    $user_uploaded_videos: RecordID
    $include_user_uploaded_videos: Boolean = false
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderBaseDetails
      account {
        id
      }
      giftable_event_addon_types(exclude_purchased_event_addon_types: true)
    }
    me {
      id
      contributor_hierarchy_items(album_folder_id: $id, slice: { limit: 999 }) {
        records {
          ... on ContributorHierarchyAlbumItem {
            album {
              ...AlbumListingDetails
              ...AlbumForReview
              album_folder_tags(slice: { limit: 999 }) {
                total
                records {
                  ...AlbumAlbumFolderTagDetails
                }
              }
              user_uploaded_videos: videos(
                processed_only: false
                uploader_ids: [$user_uploaded_videos]
              ) @include(if: $include_user_uploaded_videos) {
                total
              }
            }
          }
          ... on ContributorHierarchyAlbumFolderItem {
            album_folder {
              ...AlbumFolderBaseDetails
              ...AlbumFolderChildrenCount
              cover_photos(slice: { limit: 1 }) {
                records {
                  id
                  faces_box
                  thumbnail_urls(ignore_watermark: true) {
                    ...PhotoUrlTypeFields
                  }
                }
              }
            }
          }
        }
        total
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumListingDetails}
  ${AlbumForReview}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderChildrenCount}
  ${AlbumAlbumFolderTagDetails}
`;

export const AlbumFolderOverallPhotosDetails = gql`
  query albumFolderOverallPhotosDetails($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      individual_albums
      ...AlbumFolderPhotos
      ...AlbumFolderPhotosDetails
    }
  }
  ${AlbumFolderPhotos}
  ${AlbumFolderPhotosDetails}
`;

export const AlbumFolderForProcessing = gql`
  query albumFolderForProcessing($id: RecordID!, $photoIds: [RecordID] = []) {
    albumFolder(id: $id) {
      id
      root {
        id
      }
      name
      shoot_type
      individual_albums
      ...AlbumFolderPhotos
      unprocessed_photos: photos(
        processed_photos: false
        album_folder_photo_ids: $photoIds
      ) {
        total
      }
      processed_photos: photos(
        processed_photos: true
        album_folder_photo_ids: $photoIds
      ) {
        total
      }
      org_pay_photo_pricing_enabled
      upload_mode
      album_type
      album_subtype
      purchased_event_addon_types
      storage {
        max
        used
      }
      photo_sharing_mode
      ...AlbumFolderPhotoFinderDetails
    }
  }
  ${AlbumFolderPhotos}
  ${AlbumFolderPhotoFinderDetails}
`;

export const AlbumFolderWithPhotosByUploadId = gql`
  query albumFolderWithPhotosByUploadId(
    $id: RecordID!
    $photoItemIds: [RecordID]
    $photoBatchId: RecordID
    $limit: Int
    $offset: Int
  ) {
    albumFolder(id: $id) {
      id
      name
      photos: photos(
        processed_photos: true
        album_folder_photo_ids: $photoItemIds
        slice: { limit: $limit, offset: $offset }
        photo_batch_id: $photoBatchId
      ) {
        total
        count
        records {
          id
          status
          photo {
            ...PhotoThumbnails
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumFolderWithChildrenAndSubscription = gql`
  query albumFolderWithChildrenAndSubscription(
    $id: RecordID!
    $identity_id: RecordID
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      discovery_selection_info
      album_price_mode
      join_requires_purchase
      prepaid_digital_bundles_enabled
      prepaid_print_packages_enabled
      prepaid_photo_pricings_enabled
      prepaid_custom_prints_enabled
      album_delivery_modes {
        photo_finder
        shutterbug
      }
      album_identity_access
      account {
        ...UserDetailsWithImages
      }
      tags(slice: { limit: 1 }, album_folder_tag_types: [GRADE]) {
        records {
          id
          ... on IAlbumFolderTag {
            type
          }
        }
      }
      root {
        ...AlbumFolderDetails
        join_requires_purchase
        prepaid_digital_bundles_enabled
        prepaid_print_packages_enabled
        prepaid_photo_pricings_enabled
        prepaid_custom_prints_enabled
        album_delivery_modes {
          photo_finder
          shutterbug
        }
        tags(slice: { limit: 1 }, album_folder_tag_types: [GRADE]) {
          records {
            id
            ... on IAlbumFolderTag {
              type
            }
          }
        }
      }
      children {
        album_folders(joins_enabled_only: true, identity_id: $identity_id) {
          records {
            ...AlbumFolderDetails
            children {
              album_folders {
                total
              }
              albums(joins_enabled_only: true, identity_id: $identity_id) {
                total
                records {
                  id
                  join_code
                  tag_joins_disabled
                }
              }
            }
          }
        }
        albums(joins_enabled_only: true, identity_id: $identity_id) {
          records {
            ...AlbumDetailsWithFolderSubscription
          }
        }
      }
    }
  }
  ${UserDetailsWithImages}
  ${AlbumFolderDetails}
  ${AlbumDetailsWithFolderSubscription}
`;

export const AlbumFolderWithCreatorDetails = gql`
  query albumFolderWithCreatorDetails($id: RecordID!) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      ...AlbumFolderChildrenDetails
      ...AlbumFolderAlbumCustomPrintForPurchaseDetails
      account {
        ...UserDetailsWithImages
      }
      root {
        ...AlbumFolderBaseDetails
      }
      album_price_mode
      album_delivery_modes {
        photo_finder
        shutterbug
      }
      album_identity_access
      join_requires_purchase
      checkout_disabled
      prepaid_digital_bundles_enabled
      prepaid_print_packages_enabled
      prepaid_photo_pricings_enabled
      prepaid_custom_prints_enabled
    }
  }
  ${UserDetailsWithImages}
  ${AlbumFolderDetails}
  ${AlbumFolderChildrenDetails}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderAlbumCustomPrintForPurchaseDetails}
`;

export const AlbumFolderWithCreatorBrandDetails = gql`
  query albumFolderWithCreatorBrandDetails($id: RecordID!) {
    albumFolder(id: $id) {
      ...AlbumFolderBaseDetails
      account {
        ...UserDetailsWithImages
      }
      root {
        ...AlbumFolderBaseDetails
        album_price_mode
        album_delivery_modes {
          ppa
          shutterbug
          photo_finder
          free_web_gallery
        }
        all_photos_mode
        picture_day_enabled
        join_requires_purchase
        prepaid_digital_bundles_enabled
        prepaid_print_packages_enabled
        prepaid_photo_pricings_enabled
        prepaid_custom_prints_enabled
        start_date
      }
      album_price_mode
      album_delivery_modes {
        ppa
        shutterbug
        photo_finder
        free_web_gallery
      }
      album_identity_access
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      custom_onboarding_url
      checkout_disabled
      cover_photos(custom_cover_photos_only: true) {
        records {
          id
          faces_box
          thumbnail_urls {
            width
            height
            url
            size_code
          }
        }
      }
      is_outdated
      all_photos_mode
      picture_day_enabled
      join_requires_purchase
      prepaid_digital_bundles_enabled
      prepaid_print_packages_enabled
      prepaid_photo_pricings_enabled
      prepaid_custom_prints_enabled
      children {
        albums(slice: { limit: 1 }) {
          records {
            id
          }
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${UserDetailsWithImages}
  ${AutoContributorSettingsDetails}
`;

export const AlbumFolderIdentityAccess = gql`
  query albumFolderIdentityAccess($id: RecordID!) {
    albumFolder(id: $id) {
      id
      album_identity_access
    }
  }
`;

export const AlbumFolderWithPrepaidItemsInfo = gql`
  query albumFolderPrepaidItemsInfo($id: RecordID!) {
    albumFolder(id: $id) {
      id
      picture_day_enabled
      join_requires_purchase
      prepaid_digital_bundles_enabled
      prepaid_print_packages_enabled
      prepaid_photo_pricings_enabled
      prepaid_custom_prints_enabled
      has_album_photos
      album_folder_print_packages {
        total
      }
      photo_pricing {
        id
      }
      album_custom_prints(unique_custom_prints: true) {
        total
      }
      active_promo_codes {
        total
        records {
          ...PromoCodeDetails
        }
      }
    }
  }
  ${PromoCodeDetails}
`;

export const AlbumFolderWithPurchasableItems = gql`
  query albumFolderWithPurchasableItems($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      account {
        ...VendorDetails
      }
      all_photos_mode
      matching_disabled
      picture_day_enabled
      join_requires_purchase
      prepaid_digital_bundles_enabled
      prepaid_print_packages_enabled
      prepaid_photo_pricings_enabled
      prepaid_custom_prints_enabled
      price {
        value
        currency
      }
      web_gallery_price_per_photo {
        value
        currency
      }
      limited_time_offer_price {
        value
        currency
      }
      limited_time_offer_active
      limited_time_offer_date_start
      limited_time_offer_date_end
      album_folder_print_packages(slice: { limit: 999 }) {
        total
        records {
          id
          print_package {
            id
            soft_deleted_at
            ...PrintPackageForPurchaseDetails
          }
        }
      }
      photo_pricing {
        ...PhotoPricingForPurchaseDetails
      }
      album_custom_prints(slice: { limit: 999 }, unique_custom_prints: true) {
        total
        records {
          ...AlbumCustomPrintForPurchaseDetails
        }
      }
      album_folder_custom_prints(slice: { limit: 999 }) {
        total
        records {
          ...AlbumFolderCustomPrintForPurchaseDetails
        }
      }
      digital_bundles(slice: { limit: 999 }) {
        total
        records {
          ...DigitalBundleDetails
        }
      }
      active_promo_codes {
        total
        records {
          ...PromoCodeDetails
        }
      }
      green_screen_enabled
      products_display_order {
        all_products
        digital_products
      }
      ...AlbumFolderPrintOrderConfiguration
      ...AlbumFolderPrintAssets
    }
  }
  ${VendorDetails}
  ${PrintPackageForPurchaseDetails}
  ${PhotoPricingForPurchaseDetails}
  ${AlbumCustomPrintForPurchaseDetails}
  ${AlbumFolderCustomPrintForPurchaseDetails}
  ${PromoCodeDetails}
  ${DigitalBundleDetails}
  ${AlbumFolderPrintOrderConfiguration}
  ${AlbumFolderPrintAssets}
`;

export const AlbumFolderForTeamPhotosOrientationCheck = gql`
  query AlbumFolderForTeamPhotosOrientationCheck($id: RecordID!) {
    albumFolder(id: $id) {
      id
      custom_prints(slice: { limit: 999 }) {
        records {
          id
          name
        }
      }
      photo_pricing {
        id
        print_product_items(slice: { limit: 999 }) {
          records {
            id
            print_product_configuration {
              id
              print_product {
                id
                name
              }
              print_product_size {
                id
                print_size {
                  id
                  name
                }
              }
            }
          }
        }
      }
      tags(slice: { limit: 999 }) {
        total
        records {
          ...AlbumFolderTagDetails
        }
      }
    }
  }
  ${AlbumFolderTagDetails}
`;

export const AlbumFolderWithAlbumsAndPhotoGroups = gql`
  query albumFolderWithAlbumsAndPhotoGroups($id: RecordID!) {
    albumFolder(id: $id) {
      ...AlbumFolderWithChildrenData
    }
  }
  ${AlbumFolderWithChildrenData}
`;

export const AlbumFolderWithPhotosAndAlbumsAndPhotoGroups = gql`
  query albumFolderWithPhotosAndAlbumsAndPhotoGroups(
    $id: RecordID!
    $limit: Int
    $offset: Int
  ) {
    albumFolder(id: $id) {
      photos: photos(
        processed_photos: true
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        count
        records {
          id
          status
          created_at
          photo {
            ...PhotoThumbnails
          }
        }
      }
      ...AlbumFolderWithChildrenData
    }
  }
  ${AlbumFolderWithChildrenData}
  ${PhotoThumbnails}
`;

export const MeWithIndividualAlbumsFolder = gql`
  query meWithIndividualAlbumsFolder(
    $album_type: AlbumTypesType
    $album_subtype: AlbumSubtypesType
  ) {
    me {
      id
      individual_albums_album_folder(
        album_type: $album_type
        album_subtype: $album_subtype
      ) {
        ...AlbumFolderDetails
        ...AlbumFolderPhotosDetails
        tags(slice: { limit: 999 }) {
          records {
            ...AlbumFolderTagDetails
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderPhotosDetails}
  ${AlbumFolderTagDetails}
`;

export const AlbumFolderWithIdentityAccess = gql`
  query albumFolderWithIdentityAccess($id: RecordID!) {
    albumFolder(id: $id) {
      ...AlbumFolderBaseDetails
      ...AlbumFolderAlbumCustomPrintForPurchaseDetails
      account {
        ...UserDetails
      }
      album_identity_access
      album_price_mode
      album_delivery_modes {
        photo_finder
        shutterbug
      }
      tags(slice: { limit: 1 }, album_folder_tag_types: [GRADE]) {
        records {
          id
          ... on IAlbumFolderTag {
            type
          }
        }
      }
      children {
        album_folders(joins_enabled_only: true) {
          records {
            id
            children {
              album_folders {
                total
              }
              albums(joins_enabled_only: true) {
                total
                records {
                  id
                  join_code
                  tag_joins_disabled
                }
              }
            }
          }
        }
        albums(joins_enabled_only: true) {
          records {
            id
            join_code
            tag_joins_disabled
          }
        }
      }
      root {
        ...AlbumFolderBaseDetails
        ...AlbumFolderAlbumCustomPrintForPurchaseDetails
        tags(slice: { limit: 1 }, album_folder_tag_types: [GRADE]) {
          records {
            id
            ... on IAlbumFolderTag {
              type
            }
          }
        }
      }
    }
  }
  ${UserDetails}
  ${AlbumFolderBaseDetails}
  ${AlbumFolderAlbumCustomPrintForPurchaseDetails}
`;

export const AlbumFolderForUCPMutations = gql`
  query albumFolderForUCPMutations($id: RecordID!) {
    albumFolder(id: $id) {
      ...AlbumFolderWithChildrenData
      ...AlbumFolderPhotos
      ...AlbumFolderPhotosDetails
    }
  }
  ${AlbumFolderWithChildrenData}
  ${AlbumFolderPhotos}
  ${AlbumFolderPhotosDetails}
`;

export const AlbumFolderForSelectAllPhotos = gql`
  query albumFolderForSelectAllPhotos($id: RecordID!) {
    albumFolder(id: $id) {
      id
      photos: photos(
        processed_photos: true
        slice: { limit: 9999, offset: 0 }
      ) {
        records {
          id
        }
      }
    }
  }
`;

// @TODO Add joins_enabled_only filter once that has been implemented for AlbumFoolderChildren.album_folders
export const AlbumFolderHierarchy = gql`
  query albumFolderHierarchy($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      parent {
        id
      }
      children {
        album_folders(slice: { limit: 9999 }) {
          records {
            id
            name
            display_order
            parent {
              id
            }
          }
        }
      }
      account {
        ...UserDetailsWithImages
      }
      album_folder_hierarchy {
        records {
          id
          name
          parent {
            id
          }
          is_root
          children {
            albums {
              total
            }
            album_folders(slice: { limit: 9999 }) {
              total
              records {
                id
                name
                parent {
                  id
                }
                children {
                  albums {
                    total
                  }
                  album_folders {
                    total
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${UserDetailsWithImages}
`;

export const AlbumFolderForBrowse = gql`
  query albumFolderForBrowse($id: RecordID!) {
    albumFolder(id: $id) {
      id
      root {
        id
        name
        parent {
          id
        }
        children {
          albums(slice: { limit: 9999 }, joins_enabled_only: true) {
            total
            records {
              id
              name
              album_folder {
                id
              }
            }
          }
          album_folders(slice: { limit: 9999 }, joins_enabled_only: true) {
            total
            records {
              id
              name
              parent {
                id
              }
              children {
                albums(slice: { limit: 9999 }, joins_enabled_only: true) {
                  total
                  records {
                    id
                    name
                    album_folder {
                      id
                    }
                  }
                }
                album_folders(
                  slice: { limit: 9999 }
                  joins_enabled_only: true
                ) {
                  total
                  records {
                    id
                    name
                    parent {
                      id
                    }
                    children {
                      album_folders(joins_enabled_only: true) {
                        total
                      }
                      albums(joins_enabled_only: true) {
                        total
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const AlbumFolderWithSelectedIdentitiesForIdentity = gql`
  query albumFolderWithSelectedIdentitiesForIdentity(
    $id: RecordID!
    $album_folder_id: RecordID!
    $identity_ids: [RecordID!]
    $unique_roster_identities: Boolean
    $has_matched_photos: Boolean
    $has_yearbook_photo: Boolean
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
  ) {
    albumFolder(id: $id) {
      id
      selected_identities(
        slice: { limit: 9999 }
        identity_ids: $identity_ids
        dnp_status_filter: $dnpStatusFilter
        has_matched_photos: $has_matched_photos
        has_yearbook_photo: $has_yearbook_photo
        unique_roster_identities: $unique_roster_identities
      ) {
        total
        records {
          id
          from_invite
          created_at
          album_membership {
            id
            is_deleted
            album {
              id
              name
            }
            transaction_items {
              total
            }
          }
          identity {
            id
            name
            last_name
            relationship
            current_photo {
              ...PhotoThumbnails
            }
            current_matching_photo {
              ...PhotoThumbnails
            }
            roster_identity {
              id
              selfie_photo {
                ...PhotoThumbnails
              }
            }
            account {
              id
              name
              phone_number
              current_selfie {
                ...PhotoThumbnails
              }
            }
            ...AccountIdentityName
            ...IdentityYearbookPhotoDetails
          }
          matched_photos_extended {
            total
          }
          purchased_photos {
            total
          }
          face_highlighted_photos {
            total
          }
          cabin_name
          jersey_number
          school_class
          has_purchased_album_identity
          matched_photo_notifications(slice: { limit: 1 }) {
            total
            records {
              created_at
            }
          }
          claimed
          reference_faces(active_only: true) {
            total
            records {
              ...ReferenceFaceDetails
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AccountIdentityName}
  ${IdentityYearbookPhotoDetails}
  ${ReferenceFaceDetails}
`;

export const AlbumFolderWithIdentitiesForIdentity = gql`
  query albumFolderWIthIdentitiesForIdentity(
    $id: RecordID!
    $identity_ids: [RecordID!]
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
  ) {
    albumFolder(id: $id) {
      id
      identities(
        slice: { limit: 1 }
        identity_ids: $identity_ids
        dnp_status_filter: $dnpStatusFilter
      ) {
        total
        records {
          id
          roster_identity {
            id
            linked_identities(slice: { limit: 40 }) {
              total
              records {
                id
                account {
                  id
                  name
                  type
                  email_address
                  phone_number
                }
                identity {
                  id
                  name
                  last_name
                  matched_photo_notifications(
                    slice: { limit: 1 }
                    album_folder_id: $id
                  ) {
                    total
                    records {
                      created_at
                    }
                  }
                  has_outdated_selfie
                  selected_identities(album_folder_id: $id) {
                    total
                    records {
                      id
                      claimed
                      has_purchased_album_identity
                      album_membership {
                        id
                        transaction_items {
                          total
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const AlbumFolderForIdentityMemberships = gql`
  query albumFolderForIdentityMemberships($id: RecordID!) {
    albumFolder(id: $id) {
      roster_identity_create_enabled
      owner_purchased_joins_enabled
      ...AlbumFolderDetails
      is_demo
      default_album_delivery_mode
      org_pay_photo_pricing_enabled
      tag_join_enabled
      green_screen_enabled
      album_delivery_modes {
        photo_finder
        shutterbug
      }
      pricing_categories {
        total
        records {
          id
          pricing_category {
            id
            name
          }
        }
      }
      tags {
        total
        records {
          id
          ... on IAlbumFolderTag {
            name
            type
          }
        }
      }
      oauth_links(slice: { limit: 999 }) {
        total
        records {
          id
          oauth_account {
            id
            oauth_provider
            user_id
            user_email
            is_active
          }
          oauth_object_id
        }
      }
      face_highlighted_photos {
        total
      }
      album_photos {
        total
      }
      subscribed_album_folder_identities(without_album_joins_only: true) {
        total
      }
      ...AlbumFolderIdCardSettings
      ...AlbumFolderBroadcastSequencesDetails
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderBroadcastSequencesDetails}
  ${AlbumFolderIdCardSettings}
`;

export const AlbumFolderWithIdentities = gql`
  query albumFolderWithIdentities(
    $id: RecordID!
    $album_folder_id: RecordID!
    $dnpStatusFilter: DnpStatusFilterEnum = ALL
    $offset: Int = 0
    $limit: Int = 100
    $searchQuery: String
    $sortBy: [AlbumFolderIdentitiesSort] = []
    $matchedPhotosFilter: MatchedPhotosFilterEnum = ALL
    $transactionItemsFilter: TransactionItemsFilterEnum = ALL
    $claimedStatusFilter: ClaimedStatusFilterEnum = ALL
    $socialPostsFilter: PhotoShareBlockedFilterEnum = ALL
    $hasActiveReferenceFaces: Boolean
    $withYearbookPhoto: Boolean
    $uniqueRosterIdentities: Boolean = true
    $includePlicUploadedPhotos: Boolean = false
    $excludePrepaidConfigurations: Boolean = true
    $grade: GradeEnum
  ) {
    albumFolder(id: $id) {
      id
      flat_identities: identities(unique_roster_identities: true) {
        total
      }
      identities_with_matches: identities(
        matched_photos_filter: WITH_MATCHED_PHOTOS
      ) {
        total
      }
      identities(
        slice: { limit: $limit, offset: $offset }
        dnp_status_filter: $dnpStatusFilter
        search_query: $searchQuery
        sort_by: $sortBy
        without_invitees: true
        unique_roster_identities: $uniqueRosterIdentities
        matched_photos_filter: $matchedPhotosFilter
        transaction_items_filter: $transactionItemsFilter
        claimed_status_filter: $claimedStatusFilter
        photo_share_blocked_filter: $socialPostsFilter
        has_active_reference_faces_or_source_has: $hasActiveReferenceFaces
        with_yearbook_photo: $withYearbookPhoto
        grade: $grade
      ) {
        total
        records {
          id
          ...AlbumFolderIdentityDetails
          selected_identities(album_folder_id: $id, slice: { limit: 1 }) {
            records {
              id
              claimed
              has_purchased_album_identity
              identity {
                id
                name
                account {
                  id
                  name
                }
              }
              purchased_photos {
                total
              }
            }
          }
        }
      }
    }
  }

  ${AlbumFolderIdentityDetails}
`;

export const AlbumFolderWithSelectedIdentitiesForBroadcastTargets = gql`
  query AlbumFolderWithSelectedIdentitiesForBroadcastTargets(
    $id: RecordID!
    $matchedPhotosFilter: MatchedPhotosFilterEnum = ALL
    $transactionItemsFilter: TransactionItemsFilterEnum = ALL
    $claimedStatusFilter: ClaimedStatusFilterEnum = ALL
    $hasActiveReferenceFaces: Boolean
    $uniqueRosterIdentities: Boolean = false
  ) {
    albumFolder(id: $id) {
      id
      identities(
        slice: { limit: 9999 }
        without_invitees: true
        unique_roster_identities: $uniqueRosterIdentities
        matched_photos_filter: $matchedPhotosFilter
        transaction_items_filter: $transactionItemsFilter
        claimed_status_filter: $claimedStatusFilter
        has_active_reference_faces_or_source_has: $hasActiveReferenceFaces
      ) {
        total
        records {
          id
          account {
            id
            phone_number
          }
          roster_identity {
            id
          }
        }
      }
    }
  }
`;

export const AlbumFolderWithAlbumPhotos = gql`
  query AlbumFolderWithAlbumPhotos(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $tagIds: [RecordID]
    $filterMode: CollectionFilterModeEnum = ANY
    $uploader_ids: [RecordID]
    $account_id: RecordID
    $original_filename: String
    $search_query: String
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      album_photos(
        slice: { limit: $limit, offset: $offset }
        album_folder_tag_ids: $tagIds
        album_folder_tag_ids_mode: $filterMode
        uploader_ids: $uploader_ids
        account_id: $account_id
        original_filename: $original_filename
        search_query: $search_query
      ) {
        total
        records {
          id
          album {
            id
            name
            ...AlbumBasicDetails
            root_album_folder {
              id
            }
          }
          photo {
            ...PhotoThumbnails
            ...PhotoForegroundDetails
            original_filename
            share_blocked
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
`;

export const AlbumFolderForReview = gql`
  query AlbumFolderForReview(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $tagIds: [RecordID]
    $uploaderIds: [RecordID]
    $albumIds: [RecordID]
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      ...AlbumFolderForReviewDetails
      album_photos(
        slice: { limit: $limit, offset: $offset }
        album_folder_tag_ids: $tagIds
        album_photo_filter: REVIEWING
        uploader_ids: $uploaderIds
        album_ids: $albumIds
      ) {
        total
        records {
          id
          status
          album {
            id
            name
            ...AlbumForReview
          }
          photo {
            id
            ...PhotoThumbnails
            ...PhotoForegroundDetails
            share_blocked
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
  ${AlbumFolderForReviewDetails}
  ${AlbumForReview}
`;

export const AlbumFolderIdentityAlbumPhotos = gql`
  query albumFolderIdentityAlbumPhotos($albumFolderId: RecordID!) {
    albumFolder(id: $albumFolderId) {
      id
      pro_consumer_maybes_enabled
      ...AlbumFolderBaseDetails
      album_photos(
        slice: { limit: $limit, offset: $offset }
        identity_ids: [$identityId]
        plic_uploaded_only: $plic_uploaded_only
        matched_photos_sort: $matched_photos_sort
      ) {
        total
        records {
          id
          photo {
            ...PhotoThumbnails
            ...PhotoFavoriteDetails
            is_owner
            share_blocked
            original_filename
            album_oauth_link_photo_exists
            created_at
            faces(
              slice: { limit: 1 }
              exclude_machine_labeled_pub_boxes: true
              exclude_human_labeled_pub_boxes: true
            ) {
              total
              records {
                facebox
              }
            }
          }
          album {
            id
            name
            pricing_categories {
              total
              records {
                id
                pricing_category {
                  id
                  name
                }
              }
            }
            ...AlbumBasicDetails
            root_album_folder {
              id
            }
          }
          album_highlight {
            id
          }
          status
          ...AlbumPhotoFaceHighlightDetails
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
  ${AlbumBasicDetails}
  ${AlbumFolderBaseDetails}
  ${AlbumPhotoFaceHighlightDetails}
`;

export const IdentityAlbumFolderPhotos = gql`
  query identityAlbumFolderPhotos(
    $id: RecordID!
    $album_folder_id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $plic_uploaded_only: Boolean
    $matched_photos_sort: MatchedPhotosSortEnum
    $account_id: RecordID
  ) {
    identity(id: $id) {
      id
      ...IdentityRosterIdentityDetails
      matched_photos_extended(
        album_folder_id: $album_folder_id
        slice: { limit: $limit, offset: $offset }
        plic_uploaded_only: $plic_uploaded_only
        matched_photos_sort: $matched_photos_sort
      ) {
        total
        records {
          id
          is_entitled_matched_photo(account_id: $account_id)
          identity {
            id
          }
          photo {
            ...PhotoThumbnails
            ...PhotoFavoriteDetails
            ...PhotoForegroundDetails
            is_owner
            original_filename
            share_blocked
            faces(
              slice: { limit: 1 }
              exclude_machine_labeled_pub_boxes: true
              exclude_human_labeled_pub_boxes: true
            ) {
              total
              records {
                facebox
              }
            }
          }
          album {
            id
            name
            pricing_categories {
              total
              records {
                id
                pricing_category {
                  id
                  name
                }
              }
            }
            ...AlbumBasicDetails
            root_album_folder {
              id
              pricing_categories {
                total
                records {
                  id
                  pricing_category {
                    id
                    name
                  }
                }
              }
            }
          }
          ...MatchedPhotoYearbookPhotoDetails
        }
      }
    }
    albumFolder(id: $album_folder_id) {
      id
      pro_consumer_maybes_enabled
      owner_purchased_joins_enabled
      ...AlbumFolderBaseDetails
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumBasicDetails}
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
  ${PhotoForegroundDetails}
  ${IdentityRosterIdentityDetails}
  ${MatchedPhotoYearbookPhotoDetails}
`;

export const AlbumFolderContributorAssignments = gql`
  query albumFolderContributorAssignments($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      is_web_gallery
      is_demo
      contributor_assignments(
        slice: { limit: 999 }
        include_inherited: true
        include_nested: true
      ) {
        records {
          ...ContributorAssignmentBaseDetails
        }
      }
      contributor_assignment_shares(
        slice: { limit: 999 }
        include_inherited: true
        pending_only: true
        include_nested: true
        without_account_uuid: false
      ) {
        records {
          ...ContributorAssignmentShareBaseDetails
        }
      }
    }
  }
  ${ContributorAssignmentBaseDetails}
  ${ContributorAssignmentShareBaseDetails}
`;

export const AlbumFolderForRevenue = gql`
  query albumFolderForRevenue(
    $id: RecordID!
    $limit: Int = 25
    $offset: Int = 0
    $accountsFilter: AlbumFolderAccountsFilter = WITH_TRANSACTION_ITEMS
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      accounts(
        slice: { limit: $limit, offset: $offset }
        accounts_filter: $accountsFilter
      ) {
        total
        records {
          id
          account {
            id
            name
            phone_number
          }
          identities(slice: { limit: 1 }) {
            records {
              id
              name
              last_name
            }
          }
          transaction_items(slice: { limit: 999 }) {
            records {
              ...TransactionItemDetails
            }
          }
        }
      }

      transaction_items_total_price(
        include_album_folder_albums: true
        include_tax_price: true
        include_item_shipping_price: true
      ) {
        value
        currency
      }
      transaction_items_revenue(include_album_folder_albums: true) {
        gross {
          value
          currency
        }
        print_cost {
          value
          currency
        }
        shipping_cost {
          value
          currency
        }
        stripe_fee {
          value
          currency
        }
        waldo_fee {
          value
          currency
        }
        refunds {
          value
          currency
        }
        tax {
          value
          currency
        }
        vendor_tax {
          value
          currency
        }
        net {
          value
          currency
        }
        transactions_count
        avg_net_per_transaction
      }
    }
  }
  ${AlbumFolderDetails}
  ${TransactionItemDetails}
`;

export const AlbumFolderChildrenSearchForBlogLinks = gql`
  query albumFolderChildrenSearchForBlogLinks($id: RecordID!, $name: String) {
    albumFolderChildrenSearch(
      album_folder_id: $id
      name: $name
      name_search_mode: CONTAINS
      slice: { limit: 999 }
    ) {
      records {
        ... on IRecord {
          id
        }
        ... on AlbumFolderAlbumFolderChild {
          album_folder {
            id
            name
          }
        }
        ... on AlbumFolderAlbumChild {
          album {
            id
            name
          }
        }
        ... on AlbumFolderAlbumPhotoGroupChild {
          album_photo_group {
            id
            name
          }
        }
      }
    }
  }
`;

export const AlbumFolderSpotlight = gql`
  query albumFolderSpotlight(
    $id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $identityIds: [RecordID] = []
    $ratingFilter: [Int] = []
  ) {
    albumFolder(id: $id) {
      id
      ...AlbumFolderBaseDetails
      face_highlighted_photos(
        slice: { limit: $limit, offset: $offset }
        identity_ids: $identityIds
        face_highlight_ratings: $ratingFilter
      ) {
        total
        records {
          id
          ...AlbumPhotoFaceHighlightsForAlbumFolder
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumPhotoFaceHighlightsForAlbumFolder}
`;

export const AlbumFolderWithCoverPhotos = gql`
  query albumFolderWithCoverPhotos(
    $albumFolderId: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumFolder(id: $albumFolderId) {
      id
      album_subtype
      custom_cover_photos {
        records {
          id
          display_order
          photo {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
      album_photos(slice: { limit: $limit, offset: $offset }) {
        total
        count
        records {
          id
          photo {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const AlbumFolderWithRosterIdentitiesTotal = gql`
  query albumFolderWithRosterIdentitiesTotal($id: RecordID!) {
    albumFolder(id: $id) {
      id
      ...AlbumFolderBaseDetails
    }
    me {
      id
      roster_identities(
        slice: { limit: 1 }
        album_folder_ids: [$id]
        include_album_folder_albums: true
      ) {
        total
      }
    }
  }
  ${AlbumFolderBaseDetails}
`;

export const AlbumFolderOnboardingFunnelData = gql`
  query albumFolderOnboardingFunnelData($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      root {
        id
        name
        join_code
        album_type
        album_subtype
        album_price_mode
        album_delivery_modes {
          photo_finder
          shutterbug
        }
        price {
          value
          currency
        }
        pre_paid: is_demo
        join_type
        join_without_purchase
        force_join_without_purchase
        requires_album_folder_onboarding
        day_pricings_enabled
        limited_time_offer_active
        account_has_onboarded
      }
    }
  }
`;

export const AlbumFolderHierarchyBreadcrumb = gql`
  query albumFolderHierarchyBreadcrumb($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      picture_day_enabled
      individual_albums
      album_type
      account {
        id
      }
      joins_enabled
      matching_disabled
      account_hierarchy_settings {
        id
        notifications_enabled
      }
      children {
        albums(slice: { limit: 1 }) {
          records {
            id
          }
        }
        album_folders(slice: { limit: 1 }) {
          records {
            id
          }
        }
      }
      album_folder_hierarchy {
        records {
          id
          name
          individual_albums
          contributor_access {
            upload_access
            view_access
            album_create_access
            invite_enabled
          }
        }
      }
      account_favorite_photos {
        total
      }
      photos {
        total
      }
      videos(processed_only: false) {
        total
      }
    }
  }
`;

export const AlbumFolderActionableItems = gql`
  query albumFolderActionableItems(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      id
      actionable_items(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          ...ActionableItemFields
        }
      }
      name
    }
  }
  ${ActionableItemFields}
`;

export const AlbumFolderFavorites = gql`
  query albumFolderFavorites(
    $id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
    $favorited_by: FavoritedPhotosFilterEnum = ALL
  ) {
    albumFolder(id: $id) {
      id
      favorited_photos(
        slice: { limit: $limit, offset: $offset }
        favorited_by: $favorited_by
      ) {
        total
        records {
          id
          ...AlbumPhotoFaceHighlightsForAlbumFolder
        }
      }
    }
  }
  ${AlbumPhotoFaceHighlightsForAlbumFolder}
`;

export const RootAlbumFoldersForSelection = gql`
  query rootAlbumFolderForSelection {
    me {
      id
      album_folders(root_only: true, slice: { limit: 999 }) {
        total
        records {
          id
          ...AlbumFolderBaseDetails
          children {
            album_folders(slice: { limit: 999 }) {
              total
            }
            albums(slice: { limit: 999 }) {
              total
            }
          }
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
`;

export const AlbumFolderForSelection = gql`
  query albumFolderForSelection($id: RecordID!) {
    albumFolder(id: $id) {
      id
      ...AlbumFolderBaseDetails
      children {
        album_folders(slice: { limit: 999 }) {
          total
          records {
            ...AlbumFolderBaseDetails
            children {
              albums {
                total
              }
            }
          }
        }
        albums(slice: { limit: 999 }) {
          total
          records {
            ...AlbumDetails
            album_photo_groups(slice: { limit: 999 }) {
              total
              records {
                ...AlbumPhotoGroupBasicDetails
              }
            }
          }
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumDetails}
  ${AlbumPhotoGroupBasicDetails}
`;

export const AlbumFolderVideosPro = gql`
  query albumFolderVideos($id: RecordID!, $limit: Int = 25, $offset: Int = 0) {
    albumFolder(id: $id) {
      id
      name
      is_root
      parent {
        id
        video_guestbook_enabled
      }
      video_guestbook_enabled
      videos(processed_only: false, slice: { limit: $limit, offset: $offset }) {
        total
        records {
          id
          ...VideoDetails
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumFolderPictureDay = gql`
  query albumFolderPictureDay($id: RecordID!) {
    albumFolder(id: $id) {
      id
      ...AlbumFolderDetails
      album_folder_print_packages {
        records {
          id
          print_package {
            ...PrintPackageDetails
          }
        }
      }
      children {
        picture_day_album_folders: album_folders(
          picture_day_enabled_only: true
        ) {
          records {
            id
            ...AlbumFolderDetails
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${PrintPackageDetails}
`;

export const AlbumFolderHighlights = gql`
  query albumFolderHighlights(
    $id: RecordID!
    $limit: Int = 100
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      id
      account {
        id
      }
      highlights(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          id
          display_order
          photo {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
            ...PhotoFavoriteDetails
            is_owner
          }
          album {
            id
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoFavoriteDetails}
`;

export const MeForAlbumFoldersSearch = gql`
  query meForAlbumFoldersSearch(
    $name: String
    $album_type: AlbumTypesType
    $album_subtype: AlbumSubtypesType
    $album_type_subtype_filters: [AlbumTypeSubtypeFilter]
    $limit: Int = 25
    $include_tags: Boolean = true
    $exclude_album_folder_group_members: Boolean = false
    $include_individual_albums: Boolean = false
  ) {
    me {
      id
      album_folders(
        name: $name
        slice: { limit: $limit }
        root_only: true
        album_type: $album_type
        album_subtype: $album_subtype
        album_type_subtype_filters: $album_type_subtype_filters
        exclude_album_folder_group_members: $exclude_album_folder_group_members
        include_individual_albums: $include_individual_albums
      ) {
        records {
          id
          name
          join_code
          tag_join_enabled
          album_type
          album_subtype
          tags(slice: { limit: 999 }) @include(if: $include_tags) {
            records {
              ...AlbumFolderTagDetails
            }
          }
        }
      }
    }
  }

  ${AlbumFolderTagDetails}
`;

export const AlbumFolderForLinks = gql`
  query albumFolderForLinks(
    $id: RecordID!
    $limit: Int = 25
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      links(
        slice: { limit: $limit, offset: $offset }
        link_relationship: SOURCE
      ) {
        total
        records {
          id
          created_at
          album_folder_link_request {
            id
            ...AlbumFolderLinkRequestDetails
          }
          album_folder {
            id
            name
          }
          target_album_folder {
            id
            name
            account {
              id
              name
              organization_name
            }
          }
        }
      }
      target_links: links(
        slice: { limit: $limit, offset: $offset }
        link_relationship: TARGET
      ) {
        total
        records {
          id
          created_at
          album_folder_link_request {
            id
            ...AlbumFolderLinkRequestDetails
          }
          album_folder {
            id
            name
          }
          target_album_folder {
            id
            name
            account {
              id
              name
              organization_name
            }
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderLinkRequestDetails}
`;

export const AlbumFolderForPendingLinkRequests = gql`
  query albumFolderForLinks(
    $id: RecordID!
    $limit: Int = 25
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      link_requests(
        slice: { limit: $limit, offset: $offset }
        statuses: PENDING
        link_relationship: SOURCE
      ) {
        total
        records {
          id
          ...AlbumFolderLinkRequestDetails
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderLinkRequestDetails}
`;

export const MeForAlbumFolderLinkRequestProcess = gql`
  query meForAlbumFolderLinkRequestProcess(
    $album_folder_link_request_id: RecordID!
  ) {
    me {
      id
      album_folder_link_requests(
        album_folder_link_request_id: $album_folder_link_request_id
      ) {
        total
        records {
          id
          ...AlbumFolderLinkRequestDetails
        }
      }
    }
  }
  ${AlbumFolderLinkRequestDetails}
`;

export const AlbumFolderForVideoGuestBook = gql`
  query albumFolderForVideoGuestBook(
    $id: RecordID!
    $user_uploaded_videos: RecordID
    $include_user_uploaded_videos: Boolean = false
  ) {
    albumFolder(id: $id) {
      id
      name
      account {
        id
      }
      children {
        albums {
          records {
            id
            is_video_album
            is_wedding_day_album
            is_memory_book
            user_uploaded_videos: videos(
              processed_only: false
              uploader_ids: [$user_uploaded_videos]
            ) @include(if: $include_user_uploaded_videos) {
              total
            }
            videos(
              processed_only: false
              slice: { limit: 1 }
              sort_by_type: true
            ) {
              total
              records {
                id
                ...VideoDetails
              }
            }
          }
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumFolderForSpecialtyAlbumInvites = gql`
  query AlbumFolderForSpecialtyAlbumInvites($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      individual_albums
      account {
        id
        name
        organization_name
        storage {
          max
          used
        }
      }
      storage {
        max
        used
      }
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
      children {
        albums(slice: { limit: 99 }) {
          total
          records {
            id
            name
            creator {
              id
            }
            album_type
            album_subtype
            enable_external_upload
            external_uploads_require_review
            is_video_album
            is_memory_book
            is_wedding_day_album
            root_album_folder {
              id
            }
            auto_contributor_settings {
              ...AutoContributorSettingsDetails
            }
            purchased_event_addon_types
            slideshow {
              status
            }
          }
        }
      }
    }
  }
  ${AlbumFolderEventSettingsFields}
  ${AutoContributorSettingsDetails}
`;

export const AlbumFolderForIndividualAlbums = gql`
  query AlbumFolderForIndividualAlbums($id: RecordID!, $user_id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      individual_albums
      account {
        id
        name
        organization_name
      }
      children {
        albums(slice: { limit: 1 }) {
          total
          records {
            ...AlbumListingDetails
            account_hierarchy_settings {
              id
              notifications_enabled
            }
            user_uploaded_videos: videos(
              processed_only: false
              uploader_ids: [$user_id]
            ) {
              total
            }
          }
        }
      }
    }
  }
  ${AlbumListingDetails}
`;

export const AlbumFolderForGiftingEventAddonItems = gql`
  query AlbumFolderForGiftingEventAddonItems(
    $id: RecordID!
    $exclude_purchased_event_addon_types: Boolean = false
  ) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      account {
        id
        name
        organization_name
      }
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
      giftable_event_addon_types(
        exclude_purchased_event_addon_types: $exclude_purchased_event_addon_types
      )
    }
  }
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumFolderForOwnershipCheck = gql`
  query AlbumFolderForOwnershipCheck($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      account {
        id
        name
        organization_name
      }
    }
  }
`;

export const AlbumFolderForReferenceAlbumsCheck = gql`
  query AlbumFolderForReferenceAlbumsCheck($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      account {
        id
        name
        organization_name
      }
      reference_photo_albums {
        total
        records {
          ...AlbumListingDetails
        }
      }
    }
  }
  ${AlbumListingDetails}
`;

export const AlbumFolderForHierarchyMembers = gql`
  query AlbumFolderForHierarchyMembers(
    $id: RecordID!
    $limit: Int
    $offset: Int
  ) {
    albumFolder(id: $id) {
      id
      ...AlbumFolderBaseDetails
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      contributor_assignment_shares(
        include_inherited: true
        pending_only: true
        include_nested: true
        without_account_uuid: false
      ) {
        total
      }
      auto_contributor_invitations(status: PENDING) {
        total
      }
      hierarchy_members(slice: { limit: $limit, offset: $offset }) {
        records {
          id
          name
          username
          avatar_photo {
            id
            thumbnail_urls {
              url
              size_code
              width
              height
            }
          }
          contributor_assignments(slice: { limit: 999 }) {
            records {
              ...ContributorAssignmentBaseDetails
            }
          }
          matched_photos_count
          uploaded_photos_count
          uploaded_videos_count
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${ContributorAssignmentBaseDetails}
  ${AutoContributorSettingsDetails}
`;

export const AlbumFolderForStorageInfo = gql`
  query AlbumFolderForStorageInfo($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      purchased_event_addon_types
      expiration_date
      storage {
        max
        used
      }
      created_at
    }
  }
`;
export const AlbumFolderForPhotoFinderBalanceInfo = gql`
  query AlbumFolderForPhotoFinderBalanceInfo($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      purchased_event_addon_types
      photo_finder {
        hierarchy_credits
        credits_balance {
          total
        }
        photos_count
        pending_photos_count: photos_count(pending_charge_only: true)
      }
      created_at
    }
  }
`;

export const AlbumFolderForPendingAutoContributorInvitations = gql`
  query AlbumFolderPendingAutoContributorInvitations($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      auto_contributor_invitations(status: PENDING) {
        total
        records {
          id
          status
          email_address
          phone_number
          sender_account {
            id
            name
          }
        }
      }
      created_at
    }
  }
`;

export const AlbumFolderForPendingAutoSpotlightGenerationCheck = gql`
  query AlbumFolderForPendingAutoSpotlightGenerationCheck($id: RecordID!) {
    albumFolder(id: $id) {
      id
      has_pending_auto_face_highlights_create
    }
  }
`;

export const AlbumFolderForHierarchyMembersSettings = gql`
  query AlbumFolderForHierarchyMembersSettings($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      member_uploads_require_review
      external_uploads_require_review
      ...AlbumFolderContributeReminderDetails
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
    }
  }
  ${AlbumFolderContributeReminderDetails}
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumFolderForExpiration = gql`
  query AlbumFolderForExpiration($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      expiration_status
      expiration_date
    }
  }
`;

export const AlbumFolderForAlbumSelect = gql`
  query AlbumFolderForAlbumSelect($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      join_type
      children {
        album_folders {
          total
          records {
            id
            children {
              album_folders {
                total
                records {
                  id
                  children {
                    albums {
                      total
                      records {
                        id
                        name
                      }
                    }
                  }
                }
              }
              albums {
                total
                records {
                  id
                  name
                }
              }
            }
          }
        }
        albums {
          total
          records {
            id
            name
          }
        }
      }
    }
  }
`;

export const AlbumFolderForCustomPrintsOrders = gql`
  query albumFolderForCustomPrintsOrders(
    $id: RecordID!
    $limit: Int
    $offset: Int
    $orderStatus: [CustomPrintOrderStatusEnum]
    $customPrintTypes: CustomPrintTypeEnum
  ) {
    albumFolder(id: $id) {
      ...AlbumFolderDetails
      picture_day_enabled
      purchased_custom_prints(
        slice: { limit: $limit, offset: $offset }
        order_statuses: $orderStatus
        custom_print_type: $customPrintTypes
      ) {
        total
        records {
          ...PurchasedCustomPrintFields
        }
      }

      pending: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [PENDING]
        custom_print_type: $customPrintTypes
      ) {
        total
      }

      shipped: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [SHIPPED]
        custom_print_type: $customPrintTypes
      ) {
        total
      }

      canceled: purchased_custom_prints(
        slice: { limit: 1 }
        order_statuses: [CANCELED]
        custom_print_type: $customPrintTypes
      ) {
        total
      }
    }
  }
  ${AlbumFolderDetails}
  ${PurchasedCustomPrintFields}
`;

export const MeForAlbumFolderMarketMaxSearch = gql`
  query meForAlbumFolderMarketMaxSearch(
    $name: String
    $created_at_date_period: DatePeriodEnum
    $created_at_date_range: DateFilterInput
    $album_type_subtype_web_app_basename_filters: [AlbumTypeSubtypeWebAppBasenameFilter]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    me {
      id
      album_folders(
        name: $name
        root_only: true
        created_at_date_period: $created_at_date_period
        created_at_date_range: $created_at_date_range
        album_type_subtype_web_app_basename_filters: $album_type_subtype_web_app_basename_filters
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        records {
          id
        }
      }
    }
  }
`;

export const AlbumFolderForMarketMax = gql`
  query albumFolderForMarketMax($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      start_date
      first_photo_published_at
      accounts_with_purchases: joined_accounts_count(
        transaction_items_filter: WITH_TRANSACTION_ITEMS
      )
      accounts_without_purchases: joined_accounts_count(
        transaction_items_filter: WITHOUT_TRANSACTION_ITEMS
      )
      broadcast_sequences(has_pending_step_scheduled_within_days: 1) {
        total
        records {
          id
          broadcast_sequence {
            id
            name
          }
        }
      }
      enrolled_broadcast_sequences: broadcast_sequences(
        has_pending_step_scheduled_within_days: 730
      ) {
        total
        records {
          id
          broadcast_sequence {
            id
            name
          }
        }
      }
      last_broadcast_recipient {
        id
        created_at
        updated_at
      }
    }
  }
`;

export const AlbumFolderForHierarchyGrades = gql`
  query AlbumFolderForHierarchyGrades($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      ...AlbumFolderHierarchyGradeDetails
    }
    me {
      id
      albums(root_album_folder_ids: [$id], slice: { limit: 999 }) {
        total
        records {
          id
          ...AlbumHierarchyGradeDetails
        }
      }
    }
  }
  ${AlbumFolderHierarchyGradeDetails}
  ${AlbumHierarchyGradeDetails}
`;

export const AlbumFolderForYearbookSettings = gql`
  query AlbumFolderForYearbookSettings($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      ...AlbumFolderYearbookSettings
    }
  }
  ${AlbumFolderYearbookSettings}
`;

export const AlbumFolderForYearbookExports = gql`
  query AlbumFolderForYearbookExports($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      yearbook_exports(slice: { limit: 999 }) {
        total
        records {
          ...YearbookExportDetails
        }
      }
    }
  }
  ${YearbookExportDetails}
`;

export const AlbumFolderIdentitiesForYearbookPhotos = gql`
  query AlbumFolderIdentitiesForYearbookPhotos(
    $id: RecordID!
    $album_folder_id: RecordID!
    $with_yearbook_photo: Boolean
    $matched_photos_filter: MatchedPhotosFilterEnum
    $limit: Int = 100
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      id
      name
      identities(
        slice: { offset: $offset, limit: $limit }
        unique_roster_identities: false
        with_yearbook_photo: $with_yearbook_photo
        matched_photos_filter: $matched_photos_filter
      ) {
        total
        records {
          id
          name
          last_name
          ...IdentityHasRosterIdentityDetails
          current_face {
            id
          }
          current_matching_face {
            id
          }
          selected_identities(album_folder_id: $id) {
            total
            records {
              id
              album_membership {
                id
                album {
                  id
                  name
                }
              }
            }
          }
          matched_photos_extended(
            album_folder_id: $id
            slice: { limit: 1 }
            matched_photos_sort: BEST_SCORE
          ) {
            total
            records {
              id
              photo {
                id
                thumbnail_urls {
                  url
                  width
                  height
                  size_code
                }
              }
            }
          }
          ...IdentityYearbookPhotoDetails
        }
      }
    }
  }
  ${IdentityHasRosterIdentityDetails}
  ${IdentityYearbookPhotoDetails}
`;

export const AlbumFolderIdentitiesForYearbookPhotosHeader = gql`
  query AlbumFolderIdentitiesForYearbookPhotosHeader($id: RecordID!) {
    albumFolder(id: $id) {
      id
      identities_for_export: identities(
        unique_roster_identities: false
        with_yearbook_photo: true
      ) {
        total
      }
      identities_for_selection: identities(
        unique_roster_identities: false
        with_yearbook_photo: false
        matched_photos_filter: WITH_MATCHED_PHOTOS
      ) {
        total
      }
      identities_for_matching: identities(
        unique_roster_identities: false
        with_yearbook_photo: false
        matched_photos_filter: WITHOUT_MATCHED_PHOTOS
      ) {
        total
      }
    }
  }
`;

export const AlbumFolderForHierarchyShippingSettings = gql`
  query AlbumFolderForHierarchyShippingSettings($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      checkout_disabled
      print_partner {
        id
        api_type
      }
      account {
        id
        email_address
      }
      ...AlbumFolderHierarchyShippingSettings
    }
  }
  ${AlbumFolderHierarchyShippingSettings}
`;

export const AlbumFolderForAlbumCreate = gql`
  query AlbumFolderForAlbumCreate($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      root {
        id
        name
        privacy
        invite_only_access
        start_date
        end_date
        is_demo
        shoot_type
        tag_join_enabled
        skip_subscription_setup_prompt
        enable_external_upload
        search_subscription_credits_joins_enabled
        org_pay_photo_pricing_enabled
        picture_day_enabled
        photo_sharing_mode
        album_subtype
        album_type
        album_price_mode
        default_album_delivery_mode
        default_matches_only_setting
        is_web_gallery
        joins_enabled
        price {
          currency
          value
        }
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
        location {
          ...AlbumLocationFields
        }
        tags(album_folder_tag_types: [UNIFORM]) {
          total
          records {
            ... on UniformAlbumFolderTag {
              id
              name
              uniform_enabled
              uniform_numbered
              text_matching_enabled
            }
          }
        }
        green_screen_enabled
        pricing_categories(slice: { limit: 99 }) {
          total
          records {
            id
            pricing_category {
              id
              name
            }
          }
        }
        grades(slice: { limit: 99 }) {
          total
          records {
            id
            grade
            classes {
              total
              records {
                id
                class_name
              }
            }
          }
        }
      }
    }
  }
  ${AutoContributorSettingsDetails}
  ${AlbumLocationFields}
`;

export const AlbumFolderIndividualAlbum = gql`
  query albumFolderIndividualAlbum($id: RecordID!) {
    albumFolder(id: $id) {
      id
      children {
        albums(slice: { limit: 1 }) {
          total
          records {
            id
          }
        }
      }
    }
  }
`;

export const AlbumFolderWithSubscribedIdentities = gql`
  query albumFolderWithSubscribedIdentities(
    $id: RecordID!
    $album_folder_id: RecordID!
    $includePlicUploadedPhotos: Boolean!
    $excludePrepaidConfigurations: Boolean!
    $offset: Int = 0
    $limit: Int = 100
  ) {
    albumFolder(id: $id) {
      id
      subscribed_album_folder_identities(
        without_album_joins_only: true
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        records {
          id
          identity {
            ...AlbumFolderIdentityDetails
            selected_identities(album_folder_id: $id, slice: { limit: 1 }) {
              records {
                id
                claimed
                has_purchased_album_identity
                identity {
                  id
                  name
                  account {
                    id
                    name
                  }
                }
                purchased_photos {
                  total
                }
              }
            }
          }
        }
      }
    }
  }

  ${AlbumFolderIdentityDetails}
`;

export const AlbumFolderForAlbumPhotosTagCount = gql`
  query albumFolderForAlbumPhotosTagCount(
    $id: RecordID!
    $album_folder_tag_ids: [RecordID]
    $album_folder_tag_ids_mode: CollectionFilterModeEnum
  ) {
    albumFolder(id: $id) {
      id
      album_photos(
        album_folder_tag_ids: $album_folder_tag_ids
        album_folder_tag_ids_mode: $album_folder_tag_ids_mode
      ) {
        total
      }
    }
  }
`;

export const AlbumFolderForMarketingAccounts = gql`
  query albumFolderForMarketingAccounts($id: RecordID!) {
    albumFolder(id: $id) {
      id
      account {
        id
        logo_image {
          id
          url
        }
      }
    }
  }
`;
